import { z } from 'zod'

import { formatPrice } from '@/shared/lib/utils/price/format-price'

type SpecialPromotionDiscountType = 'percent' | 'absolute'

export type SpecialPromotionPrice = {
	VALUE: number
	PRICE_TYPE?: string
}

const SpecialPromotionRawPriceSchema = z.union([
	z.number(),
	z.object({
		VALUE: z.number(),
		PRICE_TYPE: z.string().optional()
	})
])

const SpecialPromotionRawProductSchema = z.object({
	ID: z.number(),
	KOD: z.string().optional(),
	TITLE: z.string(),
	IMAGE: z.string(),
	PRICE: SpecialPromotionRawPriceSchema,
	OLD_PRICE: SpecialPromotionRawPriceSchema.optional()
})

const SpecialPromotionResponseSchema = z
	.object({
		MAX_DISCOUNT: z.union([z.string(), z.number()]),
		DISCOUNT_TYPE: z.string().optional(),
		ITEMS: z.array(
			z.object({
				DISCOUNT: z.union([z.string(), z.number()]),
				TEXT: z.string().optional(),
				PRODUCTS: z.array(SpecialPromotionRawProductSchema)
			})
		)
	})
	.nullable()

type SpecialPromotionRawPrice = z.infer<typeof SpecialPromotionRawPriceSchema>
type SpecialPromotionRawProduct = z.infer<typeof SpecialPromotionRawProductSchema>

export type SpecialPromotionProduct = Omit<SpecialPromotionRawProduct, 'PRICE' | 'OLD_PRICE'> & {
	PRICE: SpecialPromotionPrice
	OLD_PRICE?: SpecialPromotionPrice
}

export type SpecialPromotionItem = {
	discount: number
	discountLabel: string
	discountType: SpecialPromotionDiscountType
	text?: string
	products: SpecialPromotionProduct[]
}

export type SpecialPromotion = {
	discount: number
	discountLabel: string
	discountType: SpecialPromotionDiscountType
	items: SpecialPromotionItem[]
	products: SpecialPromotionProduct[]
}

const normalizeSpecialPromotionPrice = (price: SpecialPromotionRawPrice): SpecialPromotionPrice =>
	typeof price === 'number' ? { VALUE: price } : { VALUE: price.VALUE, PRICE_TYPE: price.PRICE_TYPE }

const normalizeSpecialPromotionProduct = (product: SpecialPromotionRawProduct): SpecialPromotionProduct => ({
	...product,
	PRICE: normalizeSpecialPromotionPrice(product.PRICE),
	OLD_PRICE: product.OLD_PRICE ? normalizeSpecialPromotionPrice(product.OLD_PRICE) : undefined
})

const normalizeDiscountPercent = (discountPercent: string | number): string => {
	const discountPercentNumber = Number(discountPercent)

	if (!Number.isNaN(discountPercentNumber)) {
		return Number.isInteger(discountPercentNumber) ? String(discountPercentNumber) : String(discountPercent)
	}

	return String(discountPercent)
}

const getPromotionDiscountType = (discountType?: string): SpecialPromotionDiscountType =>
	discountType === 'absolute' ? 'absolute' : 'percent'

const getPromotionDiscountLabel = (discount: string | number, discountType: SpecialPromotionDiscountType): string =>
	discountType === 'absolute'
		? formatPrice(Number(discount)).replace(/\s₽$/, '\u00a0₽')
		: `${normalizeDiscountPercent(discount)}%`

export const parseSpecialPromotion = (responseData: unknown): SpecialPromotion | null => {
	const parsed = SpecialPromotionResponseSchema.safeParse(responseData)

	if (!parsed.success || !parsed.data) return null

	const discountType = getPromotionDiscountType(parsed.data.DISCOUNT_TYPE)
	const items = parsed.data.ITEMS.map(item => ({
		discount: Number(item.DISCOUNT),
		discountLabel: getPromotionDiscountLabel(item.DISCOUNT, discountType),
		discountType,
		text: item.TEXT,
		products: item.PRODUCTS.map(normalizeSpecialPromotionProduct)
	})).filter(item => item.products.length)
	const products = items.flatMap(item => item.products)

	if (!products.length) return null

	return {
		discount: Number(parsed.data.MAX_DISCOUNT),
		discountLabel: getPromotionDiscountLabel(parsed.data.MAX_DISCOUNT, discountType),
		discountType,
		items,
		products
	}
}
